import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, InputLabel, Typography, RadioGroup, FormControlLabel, Radio, Divider, useMediaQuery, Tooltip } from '@mui/material';
import { calculateTaxWithheld, DEFAULT_TAX_DECLARATION, TaxDeclaration } from '../../util/payg-withholding';
import { SlidersHorizontal } from 'lucide-react';
import CategoryPicker from '../category-picker';
import CalendarInput from '../ui/calendar-input';
import { StaffRow } from '../staff-table';
import Schedule from '../schedule';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useUser } from '../../providers/user-provider';
import theme from '../../util/theme/theme';
import { useSettings } from '../../providers/settings-provider';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

type Cycles = 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Yearly' | 'Ad-hoc' | '';

export interface AddStaffModalProps {
    open: boolean;
    isEdit?: { state: boolean; id: number | null };
    onClose: () => void;
    onConfirm: (data: Omit<StaffRow, 'id'>) => void;
}

// Australian defaults (express-server staff-contribution-service.js): Salary
// is the take-home pay and PAYG Tax is withheld on top of it - together the
// gross pay, which Super is calculated from.
const SUPER_GUARANTEE_RATE = 0.12;
const round2 = (n: number) => Math.round(n * 100) / 100;

const AddStaffModal: React.FC<AddStaffModalProps> = ({ open, onClose, onConfirm, isEdit }) => {
    const { user } = useUser();
    const { formatAmount } = useSettings();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [note, setNote] = useState('');
    const [employeeCommencementDate, setEmployeeCommencementDate] = useState(new Date().toISOString().split('T')[0]);
    const [cycle, setCycle] = useState<Cycles>('Monthly');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [paidOn, setPaidOn] = useState('');
    const [contributionRows, setContributionRows] = useState<string[]>([]);
    // Contribution name -> 'salary' | 'payg' | 'super' for the Australian defaults.
    const [systemKeys, setSystemKeys] = useState<Record<string, string>>({});
    const [focusedAmountIndex, setFocusedAmountIndex] = useState<number | null>(null);
    const [errors, setErrors] = useState<{ name?: string, role?: string, cycle?: string, startDate?: string }>({})
    const [contributions, setContributions] = useState<{ 
        amount?: number, 
        categoryId?: number, 
        name?: string 
    }[]>([]);

    const [fetchContributions, setFetchContributions] = useApi();
    const [fetchStaff, setFetchStaff] = useApi();

    React.useEffect(() => {
        if (isEdit?.state && open) {
            const { url, method } = buildApiRequestConfig(routes.staff.fetchById, [{ param: 'id', value: isEdit.id }]);
            setFetchStaff({ url, method });
        } else if (open && !isEdit?.state) {
            // Reset form when opening modal in create mode
            setName('');
            setRole('');
            setDateOfBirth('');
            setNote('');
            setEmployeeCommencementDate(new Date().toISOString().split('T')[0]);
            setCycle('Monthly');
            setStartDate(new Date().toISOString().split('T')[0]);
            setPaidOn(`monthly-${new Date().getDate()}`);
            // The contributions already loaded (blank amounts) until the refetch
            // below lands - clearing them left the section empty meanwhile.
            const known = (fetchContributions.data || []).filter((item: any) => item.isEnabled);
            setContributions(known.map((item: any) => ({ amount: undefined, categoryId: item.categoryId, name: item.name })));
            setContributionRows(known.map((item: any) => item.name));
            setErrors({});
            setTaxDeclaration(DEFAULT_TAX_DECLARATION);
            setTaxDetailsOpen(false);
        }
        // Keyed on isEdit's values - the page passes a new object every render,
        // which would reset the form (and its contribution rows) each time.
    }, [isEdit?.state, isEdit?.id, open, setFetchStaff]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const { data, isLoading } = fetchStaff;
        if (!isLoading && data) {
            setName(data.name || '');
            setRole(data.role || '');
            setDateOfBirth(data.dateOfBirth || '');
            setNote(data.note || '');
            setCycle(data.paymentCycle || 'Daily');
            setEmployeeCommencementDate(data.commencementDate || new Date().toISOString().split('T')[0]);
            setStartDate(data.startDate || new Date().toISOString().split('T')[0]);
            setPaidOn(data.paidOn || '');
            const contributions = data.appliedContributionBreakdown || data.contributions || [];
            setContributions(contributions);
            setContributionRows(contributions.map((item: any) => item.name));
            setTaxDeclaration({ ...DEFAULT_TAX_DECLARATION, ...(data.taxDeclaration || {}) });
            setTaxDetailsOpen(false);
        }
    }, [fetchStaff]);

    React.useEffect(() => {
        if (!open) return;
        const { url: contribUrl, method: contribMethod } = buildApiRequestConfig(routes.contributions.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetchContributions({ url: contribUrl, method: contribMethod });
    }, [open, user?.entityId, isEdit?.state, setFetchContributions]); // eslint-disable-line react-hooks/exhaustive-deps


    React.useEffect(() => {
        const { data, isLoading } = fetchContributions;
        if (!isLoading && data) {
            const newRows = data.filter((item: any) => item.isEnabled).map((item: any) => item.name);
            setSystemKeys(Object.fromEntries(data.filter((item: any) => item.systemKey).map((item: any) => [item.name, item.systemKey])));
            setContributionRows(newRows);

            if (!isEdit?.state) {
                setContributions(data.filter((item: any) => item.isEnabled).map((item: any) => ({ amount: undefined, categoryId: item.categoryId, name: item.name })));
            } else {
                // Use functional form to read previous contributions without adding them to deps
                setContributions(prev => newRows.map((name: any) => {
                    const existing = prev.find((c: any) => c.name === name);
                    return existing || { amount: undefined, categoryId: data.find((item: any) => item.name === name)?.categoryId, name };
                }));
            }
        }
    }, [fetchContributions, isEdit?.state]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-derive paidOn for Quarterly from startDate
    React.useEffect(() => {
        if (cycle === 'Quarterly' && startDate) {
            const startDateObj = new Date(startDate);
            const dayOfMonth = startDateObj.getDate();
            const monthIndex = startDateObj.getMonth();

            const getSuffix = (n: number): string => {
                if (n % 100 >= 11 && n % 100 <= 13) return 'th';
                const lastDigit = n % 10;
                return ['th', 'st', 'nd', 'rd'][lastDigit] || 'th';
            };

            const getQuarter = (monthIndex: number): string => {
                if (monthIndex < 3) return 'Q1';
                if (monthIndex < 6) return 'Q2';
                if (monthIndex < 9) return 'Q3';
                return 'Q4';
            };

            const quarter = getQuarter(monthIndex);
            const newPattern = `quarterly-${quarter}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
            if (paidOn !== newPattern) {
                setPaidOn(newPattern);
            }
        }
    }, [startDate, cycle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update paidOn based on startDate and cycle, just like recurring-provision updates recurrencePattern
    React.useEffect(() => {
        if (!startDate || cycle === 'Ad-hoc' || !cycle) return;

        const startDateObj = new Date(startDate);
        const dayOfWeek = startDateObj.getDay();
        const dayOfMonth = startDateObj.getDate();
        const month = startDateObj.toLocaleString('en-US', { month: 'long' });
        const monthIndex = startDateObj.getMonth();

        const dayMap = {
            0: 'Sunday',
            1: 'Monday',
            2: 'Tuesday',
            3: 'Wednesday',
            4: 'Thursday',
            5: 'Friday',
            6: 'Saturday'
        };

        const getSuffix = (n: number): string => {
            if (n % 100 >= 11 && n % 100 <= 13) return 'th';
            const lastDigit = n % 10;
            return ['th', 'st', 'nd', 'rd'][lastDigit] || 'th';
        };

        const getQuarter = (monthIndex: number): string => {
            if (monthIndex < 3) return 'Q1';
            if (monthIndex < 6) return 'Q2';
            if (monthIndex < 9) return 'Q3';
            return 'Q4';
        };

        let newPaidOn = '';

        if (cycle === 'Weekly') {
            const dayName = dayMap[dayOfWeek as keyof typeof dayMap];
            newPaidOn = `weekly-${dayName}`;
        } else if (cycle === 'Fortnightly') {
            const dayName = dayMap[dayOfWeek as keyof typeof dayMap];
            newPaidOn = `fortnightly-${dayName}`;
        } else if (cycle === 'Monthly') {
            newPaidOn = `monthly-${dayOfMonth}`;
        } else if (cycle === 'Quarterly') {
            const quarter = getQuarter(monthIndex);
            newPaidOn = `quarterly-${quarter}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
        } else if (cycle === 'Yearly') {
            newPaidOn = `yearly-${month}-${dayOfMonth}${getSuffix(dayOfMonth)}`;
        }

        if (newPaidOn && paidOn !== newPaidOn) {
            setPaidOn(newPaidOn);
        }
    }, [startDate, cycle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync startDate based on paidOn changes (reverse direction)
    React.useEffect(() => {
        if (!paidOn || !startDate || cycle === 'Ad-hoc' || !cycle) return;

        const currentDate = new Date(startDate);
        let newStartDate = new Date(startDate);

        if (cycle === 'Weekly' || cycle === 'Fortnightly') {
            const dayMap: { [key: string]: number } = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };
            const dayName = paidOn.split('-')[1];
            const targetDay = dayMap[dayName];
            if (targetDay !== undefined) {
                const currentDayOfWeek = currentDate.getDay();
                const daysForward = (targetDay - currentDayOfWeek + 7) % 7;
                if (daysForward !== 0) {
                    const daysBackward = 7 - daysForward;
                    const move = daysBackward < daysForward ? -daysBackward : daysForward;
                    newStartDate.setDate(currentDate.getDate() + move);
                    setStartDate(newStartDate.toISOString().slice(0, 10));
                }
            }
        } else if (cycle === 'Monthly') {
            // Extract day from pattern like "monthly-15"
            const dayOfMonth = parseInt(paidOn.split('-')[1]);
            if (!isNaN(dayOfMonth)) {
                newStartDate.setDate(dayOfMonth);
                const formattedDate = newStartDate.toISOString().slice(0, 10);
                setStartDate(formattedDate);
            }
        } else if (cycle === 'Quarterly') {
            // Extract from pattern like "quarterly-Q1-15th"
            const parts = paidOn.split('-');
            const dayOfMonth = parseInt(parts[2]);
            if (!isNaN(dayOfMonth)) {
                newStartDate.setDate(dayOfMonth);
                const formattedDate = newStartDate.toISOString().slice(0, 10);
                setStartDate(formattedDate);
            }
        } else if (cycle === 'Yearly') {
            // Extract from pattern like "yearly-May-13th"
            const parts = paidOn.split('-');
            const monthName = parts[1];
            const dayOfMonth = parseInt(parts[2]);
            
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const monthIndex = months.indexOf(monthName);
            
            if (monthIndex !== -1 && !isNaN(dayOfMonth)) {
                newStartDate.setMonth(monthIndex);
                newStartDate.setDate(dayOfMonth);
                const formattedDate = newStartDate.toISOString().slice(0, 10);
                setStartDate(formattedDate);
            }
        }
    }, [paidOn, cycle]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleContributionChange = (name: string, field: 'amount' | 'categoryId', value: string | number) => {
        setContributions(prev => {
            const updated = [...prev];
            const idx = updated.findIndex(c => c.name === name);
            if (idx !== -1) {
                updated[idx] = { ...updated[idx], [field]: value };
            }
            return updated;
        });
    };

    const amountByKey = (key: string) => {
        const c = contributions.find(item => item.name && systemKeys[item.name] === key);
        return c?.amount !== undefined ? Number(c.amount) || 0 : undefined;
    };
    // Australian payroll (the company has all three of Salary, PAYG Tax and
    // Superannuation): only the Gross Payment is entered. PAYG comes from the
    // ATO tax tables for the pay cycle (util/payg-withholding.ts), Salary is
    // what's left of the gross, and Super is worked out from it below - all
    // three read-only. Ad-hoc pay has no tax-table formula, so it's off here.
    const hasAuPayroll = ['salary', 'payg', 'super'].every(k => contributionRows.some(n => systemKeys[n] === k));
    const [grossPayment, setGrossPayment] = useState('');
    // The employee's TFN / withholding declaration answers (Tax details) -
    // saved with the staff member so how their tax was worked out can be reviewed.
    const [taxDeclaration, setTaxDeclaration] = useState<TaxDeclaration>(DEFAULT_TAX_DECLARATION);
    const [taxDetailsOpen, setTaxDetailsOpen] = useState(false);
    // Filled in once per opening (an existing staff member's Salary + PAYG),
    // and only recalculated after the user changes the gross or the cycle -
    // opening a staff member never silently rewrites their saved amounts.
    const grossInitialized = React.useRef(false);
    const grossEdited = React.useRef(false);

    const applyGross = React.useCallback((grossText: string, payCycle: string, declaration: TaxDeclaration) => {
        const salaryName = contributionRows.find(n => systemKeys[n] === 'salary');
        const paygName = contributionRows.find(n => systemKeys[n] === 'payg');
        if (!salaryName || !paygName) return;
        const gross = parseFloat(grossText);
        if (!(gross > 0)) {
            setContributions(prev => prev.map(c => (c.name === salaryName || c.name === paygName ? { ...c, amount: undefined } : c)));
            return;
        }
        const payg = calculateTaxWithheld(gross, payCycle, declaration)?.total ?? null;
        setContributions(prev => {
            const withheld = payg ?? (prev.find(c => c.name === paygName)?.amount ?? 0);
            return prev.map(c => c.name === paygName ? { ...c, amount: withheld }
                : c.name === salaryName ? { ...c, amount: round2(gross - withheld) } : c);
        });
    }, [contributionRows, systemKeys]);

    React.useEffect(() => {
        if (!open) { grossInitialized.current = false; grossEdited.current = false; return; }
        if (grossInitialized.current || grossEdited.current || !hasAuPayroll) return;
        // Waits for the staff member's saved amounts - the rows can appear
        // (empty) before they load. A new staff member just stays blank.
        const gross = (amountByKey('salary') ?? 0) + (amountByKey('payg') ?? 0);
        if (!(gross > 0)) return;
        grossInitialized.current = true;
        setGrossPayment(round2(gross).toFixed(2));
    }, [open, hasAuPayroll, contributions]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (hasAuPayroll && grossEdited.current) applyGross(grossPayment, cycle, taxDeclaration);
    }, [cycle]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGrossChange = (value: string) => {
        if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
        grossEdited.current = true;
        setGrossPayment(value);
        applyGross(value, cycle, taxDeclaration);
    };

    // A Tax details answer changed - recalculate from the current gross.
    const updateTaxDeclaration = (patch: Partial<TaxDeclaration>) => {
        const next = { ...taxDeclaration, ...patch };
        setTaxDeclaration(next);
        grossEdited.current = true;
        applyGross(grossPayment, cycle, next);
    };
    const taxBreakdown = hasAuPayroll ? calculateTaxWithheld(parseFloat(grossPayment) || 0, cycle, taxDeclaration) : null;
    const isResident = taxDeclaration.residency === 'resident';
    const isWhm = taxDeclaration.residency === 'whm';
    // Questions that only apply to some employees (Schedule 1 / 8 / 15).
    const thresholdApplies = taxDeclaration.tfnProvided && isResident;
    const residentTftApplies = thresholdApplies && taxDeclaration.claimsTaxFreeThreshold;
    const studyLoanApplies = taxDeclaration.tfnProvided && !isWhm;

    const salaryAmount = amountByKey('salary');
    const superAmount = salaryAmount !== undefined ? round2((salaryAmount + (amountByKey('payg') ?? 0)) * SUPER_GUARANTEE_RATE) : undefined;

    const formatAmountDisplay = (value: number | undefined, index: number) => {
        if (focusedAmountIndex === index) {
            // While focused, show raw number
            return value || '';
        }
        // While blurred, show with 2 decimals
        if (value === undefined || value === 0) return '';
        const num = typeof value === 'string' ? parseFloat(value as string) : value;
        if (isNaN(num)) return '';
        return num.toFixed(2);
    };

    const handleAmountBlur = (index: number, event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value;
        if (value === '') {
            setFocusedAmountIndex(null);
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const formattedValue = numValue.toFixed(2);
            const updatedContributions = [...contributions];
            updatedContributions[index] = { ...updatedContributions[index], amount: parseFloat(formattedValue) };
            setContributions(updatedContributions);
        }
        setFocusedAmountIndex(null);
    };

    const validate = () => {
        const newErrors: { name?: string, role?: string, cycle?: string, startDate?: string } = {};
        if (!name.trim()) newErrors.name = 'Name is required';
        if (!cycle) newErrors.cycle = 'Payment cycle is required';
        if (!startDate) newErrors.startDate = 'First payment date is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const generatePaymentSequenceDates = (start: string, type: 'quarterly'): string[] => {
        if (!start) return [];
        const dates: string[] = [];
        const baseDate = new Date(start);
        const monthsToAdd = type === 'quarterly' ? 3 : 3;
        
        for (let i = 0; i < 4; i++) {
            const date = new Date(baseDate);
            date.setMonth(date.getMonth() + (i * monthsToAdd));
            dates.push(date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }));
        }
        return dates;
    };

    const handleConfirm = () => {
        if (!validate()) return;
        onConfirm({ 
            name, 
            role,
            dateOfBirth,
            note,
            paymentCycle: cycle,
            employeeCommencementDate,
            startDate,
            paidOn,
            contributions: contributions.map((contribution) => ({
                name: contribution.name,
                amount: contribution.name && systemKeys[contribution.name] === 'super' ? superAmount : contribution.amount,
                categoryId: contribution.categoryId ? Number(contribution.categoryId) : undefined
            })),
            ...(hasAuPayroll && { taxDeclaration }),
        });
    };

    const handleClose = () => {
        setName('');
        setRole('');
        setDateOfBirth('');
        setNote('');
        setEmployeeCommencementDate(new Date().toISOString().split('T')[0]);
        setCycle('Monthly');
        setStartDate(new Date().toISOString().split('T')[0]);
        setPaidOn('');
        setContributions([]);
        setErrors({});
        onClose();
    };

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 24, pb: 2 }}>{isEdit?.state ? 'Edit' : 'Add New'} Staff</DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <InputLabel sx={{ fontSize: 14 }}>Name <span style={{ color: 'red' }}>*</span></InputLabel>
                        <TextField 
                            fullWidth 
                            placeholder="Enter Name" 
                            value={name} 
                            onChange={e => setName(e.target.value)} 
                            size="small" 
                            error={!!errors.name}
                            helperText={errors.name}
                            sx={{ mb: 1 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <InputLabel sx={{ fontSize: 14 }}>Employee Commencement Date</InputLabel>
                        <CalendarInput
                            value={employeeCommencementDate}
                            onChange={setEmployeeCommencementDate}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <InputLabel sx={{ fontSize: 14 }}>Role</InputLabel>
                        <TextField 
                            fullWidth 
                            placeholder="Enter Role" 
                            value={role} 
                            onChange={e => setRole(e.target.value)} 
                            size="small" 
                            sx={{ mb: 1 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <InputLabel sx={{ fontSize: 14 }}>Date of Birth</InputLabel>
                        <CalendarInput
                            value={dateOfBirth}
                            onChange={setDateOfBirth}
                        />
                    </Box>
                </Box>
                <Box>
                    <InputLabel sx={{ fontSize: 14 }}>Note</InputLabel>
                    <TextField 
                        fullWidth 
                        placeholder="Enter Note"
                        value={note} 
                        onChange={e => setNote(e.target.value)} 
                        size="small" 
                        rows={2}
                        multiline
                        sx={{ mb: 2 }} />
                </Box>
                <Divider sx={{ mb: 1 }}/>
                <Box display="grid" gridTemplateColumns={isMobile ? '1fr' : '1fr 3fr'} gap={2}>
                    <Box sx={{ borderRight: isMobile ? 'none' : '1px solid #e0e0e0', borderBottom: isMobile ? '1px solid #e0e0e0' : 'none', pb: isMobile ? 1 : 0 }}>
                        <RadioGroup
                            value={cycle}
                            onChange={e => setCycle(e.target.value as Cycles)}
                        >
                            <FormControlLabel
                                value="Weekly"
                                control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                                label={<Typography sx={{ fontSize: 13 }}>Weekly</Typography>}
                            />
                            <FormControlLabel
                                value="Fortnightly"
                                control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                                label={<Typography sx={{ fontSize: 13 }}>Fortnightly</Typography>}
                            />
                            <FormControlLabel
                                value="Monthly"
                                control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                                label={<Typography sx={{ fontSize: 13 }}>Monthly</Typography>}
                            />
                            <FormControlLabel
                                value="Quarterly"
                                control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                                label={<Typography sx={{ fontSize: 13 }}>Quarterly</Typography>}
                            />
                            <FormControlLabel
                                value="Yearly"
                                control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                                label={<Typography sx={{ fontSize: 13 }}>Yearly</Typography>}
                            />
                            <Tooltip title={hasAuPayroll ? 'Not available with Australian payroll - PAYG has no tax-table formula for ad-hoc pay' : ''} placement="right">
                                <FormControlLabel
                                    value="Ad-hoc"
                                    disabled={hasAuPayroll}
                                    control={<Radio size='small' sx={{ color: 'var(--color-text-muted)', height: 25 }} />}
                                    label={<Typography sx={{ fontSize: 13 }}>Ad-hoc</Typography>}
                                />
                            </Tooltip>
                        </RadioGroup>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                            <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5, color: 'var(--color-text-muted)' }}>First Payment Date <span style={{ color: '#F44336' }}>*</span></Typography>
                            <CalendarInput
                                value={startDate}
                                onChange={setStartDate}
                                error={!!errors.startDate}
                                helperText={errors.startDate}
                            />
                        </Box>
                        { cycle && cycle !== 'Ad-hoc' && cycle !== 'Quarterly' &&
                            <Box>
                                <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5, color: 'var(--color-text-muted)' }}>Paid On <span style={{ color: '#F44336' }}>*</span></Typography>
                                <Schedule
                                    cycle={cycle}
                                    value={paidOn}
                                    onChange={setPaidOn}
                                />
                            </Box>
                        }
                        { cycle === 'Quarterly' && startDate &&
                            <Box sx={{ mb: 1.5, p: 1, border: '1px solid #bdbdbdff', borderRadius: 1, backgroundColor: 'var(--color-container-background)' }}>
                                <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5, color: 'var(--color-text-muted)' }}>Payment Dates</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'space-between' }}>
                                    {generatePaymentSequenceDates(startDate, 'quarterly').map((date, idx) => (
                                        <Typography key={idx} sx={{ fontSize: 13, color: 'var(--color-default-text)' }}>
                                            {date}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        }
                        { cycle === 'Quarterly' && !startDate &&
                            <Box sx={{ mb: 1.5, p: 1, border: '1px solid #bdbdbdff', borderRadius: 1, backgroundColor: 'var(--color-container-background)', minHeight: 20, display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: 13, color: '#9e9e9e', fontStyle: 'italic', textAlign: 'center', width: '100%' }}>
                                    Select a start date to view dates
                                </Typography>
                            </Box>
                        }
                    </Box>
                </Box>
                <Divider sx={{ mt: 1 }}/>
                <Box sx={{ mt: 2 }}>
                    <InputLabel sx={{ fontSize: 14 }}>Contributions <span style={{ color: 'red' }}>*</span></InputLabel>
                    {hasAuPayroll && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <Typography sx={{ width: 160, textAlign: 'right', fontSize: 14, fontWeight: 600 }}>
                                {/* Worded as the ATO calculator asks it. */}
                                Gross {cycle === 'Ad-hoc' ? '' : `${cycle.toLowerCase()} `}payments
                            </Typography>
                            <TextField
                                placeholder="0.00"
                                value={grossPayment}
                                onChange={e => handleGrossChange(e.target.value)}
                                onBlur={() => { const g = parseFloat(grossPayment); if (g > 0) setGrossPayment(g.toFixed(2)); }}
                                size="small"
                                sx={{ ml: 2, width: 200 }}
                                InputProps={{
                                    startAdornment: <span style={{ color: theme.palette.primary.main, fontSize: 14, marginRight: 8 }}>{formatAmount?.(0)?.split(' ')[0]}</span>,
                                }}
                            />
                            <Box sx={{ ml: 2, minWidth: 0 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<SlidersHorizontal size={14} />}
                                    onClick={() => setTaxDetailsOpen(true)}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                                >
                                    Tax details
                                </Button>
                            </Box>
                        </Box>
                    )}
                    {/* The ATO tax withheld calculator's other questions - set to the
                        common case, saved with the staff member. */}
                    {hasAuPayroll && (
                        <Dialog open={taxDetailsOpen} onClose={() => setTaxDetailsOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                            <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 0.5 }}>Tax details</DialogTitle>
                            <DialogContent sx={{ pt: '8px !important' }}>
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', mb: 2 }}>
                                    From the employee's TFN declaration and withholding declaration. Saved with the staff member.
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 0.5 }}>
                                    <TextField
                                        select size="small" label="Tax file number provided"
                                        value={taxDeclaration.tfnProvided ? 'yes' : 'no'}
                                        onChange={e => updateTaxDeclaration({ tfnProvided: e.target.value === 'yes' })}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </TextField>
                                    <TextField
                                        select size="small" label="Residency for tax"
                                        value={taxDeclaration.residency}
                                        onChange={e => updateTaxDeclaration({ residency: e.target.value as TaxDeclaration['residency'] })}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="resident">Australian resident</option>
                                        <option value="foreign">Foreign resident</option>
                                        <option value="whm">Working holiday maker</option>
                                    </TextField>
                                    <TextField
                                        select size="small" label="Claims the tax-free threshold"
                                        disabled={!thresholdApplies}
                                        value={taxDeclaration.claimsTaxFreeThreshold ? 'yes' : 'no'}
                                        onChange={e => updateTaxDeclaration({ claimsTaxFreeThreshold: e.target.value === 'yes' })}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </TextField>
                                    <TextField
                                        select size="small" label="Study / training loan (HELP, VSL...)"
                                        disabled={!studyLoanApplies}
                                        value={taxDeclaration.studyLoan ? 'yes' : 'no'}
                                        onChange={e => updateTaxDeclaration({ studyLoan: e.target.value === 'yes' })}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="no">No</option>
                                        <option value="yes">Yes</option>
                                    </TextField>
                                    <TextField
                                        select size="small" label="Medicare levy exemption"
                                        disabled={!residentTftApplies}
                                        value={taxDeclaration.medicareExemption}
                                        onChange={e => updateTaxDeclaration({ medicareExemption: e.target.value as TaxDeclaration['medicareExemption'] })}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="none">None</option>
                                        <option value="half">Half exemption</option>
                                        <option value="full">Full exemption</option>
                                    </TextField>
                                    <TextField
                                        size="small" label="Tax offsets claimed (per year)"
                                        disabled={!residentTftApplies}
                                        value={taxDeclaration.taxOffsetsAnnual || ''}
                                        placeholder="0"
                                        onChange={e => { if (/^\d*\.?\d*$/.test(e.target.value)) updateTaxDeclaration({ taxOffsetsAnnual: parseFloat(e.target.value) || 0 }); }}
                                        InputProps={{ startAdornment: <span style={{ color: theme.palette.primary.main, fontSize: 14, marginRight: 6 }}>{formatAmount?.(0)?.split(' ')[0]}</span> }}
                                    />
                                    <TextField
                                        select size="small" label="Medicare levy reduction (family)"
                                        disabled={!residentTftApplies || taxDeclaration.medicareExemption === 'full'}
                                        value={taxDeclaration.medicareReduction ? 'yes' : 'no'}
                                        onChange={e => updateTaxDeclaration({ medicareReduction: e.target.value === 'yes' })}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="no">No</option>
                                        <option value="yes">Yes</option>
                                    </TextField>
                                    <TextField
                                        size="small" label="Dependent children"
                                        disabled={!residentTftApplies || !taxDeclaration.medicareReduction || taxDeclaration.medicareExemption === 'full'}
                                        value={taxDeclaration.medicareReductionChildren || ''}
                                        placeholder="0"
                                        onChange={e => { if (/^\d*$/.test(e.target.value)) updateTaxDeclaration({ medicareReductionChildren: parseInt(e.target.value, 10) || 0 }); }}
                                    />
                                    {isWhm && (
                                        <TextField
                                            select size="small" label="Registered as a working holiday maker employer"
                                            value={taxDeclaration.whmRegisteredEmployer ? 'yes' : 'no'}
                                            onChange={e => updateTaxDeclaration({ whmRegisteredEmployer: e.target.value === 'yes' })}
                                            SelectProps={{ native: true }}
                                            sx={{ gridColumn: '1 / -1' }}
                                            helperText={taxDeclaration.whmRegisteredEmployer ? 'Withheld at 15% - assumes under $45,000 paid to them this year' : 'Withheld at foreign resident rates'}
                                        >
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                        </TextField>
                                    )}
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                                    {taxBreakdown ? <>Tax withheld: <b style={{ color: 'var(--color-text)' }}>{formatAmount ? formatAmount(taxBreakdown.total) : taxBreakdown.total}</b> per pay</> : 'Enter a gross payment to see the tax withheld'}
                                </Typography>
                                <Button onClick={() => setTaxDetailsOpen(false)} variant="contained" sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-primary)' } }}>
                                    Done
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                    <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                        <Box sx={{ minWidth: 180 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>Amount</Typography>
                        {contributionRows.map((label, idx) => {
                            const key = systemKeys[label];
                            const isSuper = key === 'super';
                            // With Australian payroll all three come from the Gross Payment.
                            const calculated = isSuper || (hasAuPayroll && (key === 'salary' || key === 'payg'));
                            const hint = hasAuPayroll
                                ? (key === 'salary' ? 'Gross − Tax Withheld' : key === 'payg' ? 'From the ATO tax tables' : isSuper ? `${SUPER_GUARANTEE_RATE * 100}% of Gross` : '')
                                : (isSuper ? `${SUPER_GUARANTEE_RATE * 100}% of Salary + PAYG` : key === 'payg' ? 'Withheld for the ATO' : '');
                            return (
                            <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                                <Tooltip title={hint} placement="left" disableHoverListener={!hint}>
                                    <Typography sx={{ width: 160, textAlign: 'right', fontSize: 14 }}>
                                        {label}
                                        {hint && <Typography component="span" sx={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', lineHeight: 1.2 }}>{hint}</Typography>}
                                    </Typography>
                                </Tooltip>
                                <TextField
                                    placeholder="0.00"
                                    disabled={calculated}
                                    value={isSuper ? (superAmount !== undefined ? superAmount.toFixed(2) : '') : formatAmountDisplay(contributions[idx]?.amount, idx)}
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                            handleContributionChange(contributionRows[idx], 'amount', val);
                                        }
                                    }}
                                    onFocus={() => setFocusedAmountIndex(idx)}
                                    onBlur={(e) => handleAmountBlur(idx, e)}
                                    size="small"
                                    sx={{ ml: 2, width: 200 }}
                                    InputProps={{
                                        startAdornment: <span style={{ color: theme.palette.primary.main, fontSize: 14, marginRight: 8 }}>{formatAmount?.(0)?.split(' ')[0]}</span>,
                                    }}
                                />
                            </Box>
                            );
                        })}
                        </Box>
                        <Box sx={{ minWidth: 220 }}>
                            <Typography sx={{ fontWeight: 600, mb: 1 }}>Category</Typography>
                            {contributionRows.map((_, idx) => (
                                <Box key={idx} sx={{ mb: 1 }}>
                                    <CategoryPicker
                                        value={contributions[idx]?.categoryId?.toString() ?? ''}
                                        onChange={val => handleContributionChange(contributionRows[idx], 'categoryId', val)}
                                        placeholder="Select category"
                                        height={40}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button onClick={handleClose} variant="contained" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                    Cancel
                </Button>
                <Button onClick={handleConfirm} variant="contained" sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 150, whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-primary)' } }}>
                    {isEdit?.state ? 'Save Changes' : 'Add Staff'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose}>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} maxWidth="md" PaperProps={{ sx: { borderRadius: 4, p: 0 } }}>
            {content}
        </Dialog>
    );
};

export default AddStaffModal;
